import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Check, Loader2, Star } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewSubmitPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState("");

  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  usePageMeta({
    path: `/review/${token}`,
    title: "Share your experience , MediTalk",
    description: "Tell us what your MediTalk experience was like.",
  });

  useEffect(() => {
    if (!token) {
      setError("This link is missing its token.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/reviews/request/${token}`);
        if (cancelled) return;
        if (data.used) {
          setError("This link has already been used. Thank you again , a member of the team will be in touch if there's anything else.");
          setLoading(false);
          return;
        }
        setCtx(data);
        setLoading(false);
      } catch (err) {
        const detail = err?.response?.data?.detail || "This link is no longer valid.";
        if (!cancelled) {
          setError(detail);
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (rating < 1 || rating > 5) {
      toast.error("Please pick a rating from 1 to 5 stars.");
      return;
    }
    if (content.trim().length < 10) {
      toast.error("Please write a few more words , just a sentence or two is fine.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews/submit`, {
        token,
        rating,
        content: content.trim(),
      });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Sorry, couldn't save your review.";
      toast.error(detail);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 size={28} className="animate-spin text-[var(--lime-dark)]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white">
        <section className="bg-[var(--dark-bg)] grain-dark py-16 md:py-24">
          <div className="container-main max-w-2xl text-center">
            <h1 className="font-['Outfit'] font-bold text-white text-3xl md:text-4xl mb-4">
              Hmm, we couldn't open this link.
            </h1>
            <p className="text-white/80 mb-8">{error}</p>
            <Link to="/" className="btn-primary">Back to MediTalk</Link>
          </div>
        </section>
      </div>
    );
  }

  if (done) {
    return (
      <div className="bg-white">
        <section className="bg-[var(--dark-bg)] grain-dark relative overflow-hidden min-h-[70vh] flex items-center">
          <div className="container-main py-16 md:py-24 relative z-10 max-w-2xl text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[var(--lime)]/12 border border-[var(--lime)]/30 mb-6">
              <Check size={26} className="text-[var(--lime)]" strokeWidth={3} />
            </div>
            <h1 className="font-['Outfit'] font-bold text-white tracking-tight text-3xl md:text-5xl mb-4 leading-[1.05]">
              Thank you, {ctx?.first_name?.split(" ")[0] || "again"}.
            </h1>
            <p className="text-base md:text-lg text-white/80 leading-relaxed max-w-xl mx-auto">
              Your review has been received. We'll publish it after a quick read so it sits alongside the other community stories.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/reviews" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[var(--lime)] text-[var(--dark-bg)] font-semibold text-sm hover:bg-[var(--lime-dark)] transition-all">
                See other reviews
              </Link>
              <Link to="/register" className="inline-flex items-center justify-center px-6 py-3 rounded-full bg-white/[0.06] text-white font-semibold text-sm border border-white/15 hover:bg-white/[0.1] transition-colors">
                Tell a friend
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const firstName = (ctx?.first_name || "").trim().split(" ")[0];

  return (
    <div className="bg-white">
      <section className="bg-[var(--dark-bg)] grain-dark relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute -top-32 -right-24 w-[420px] h-[420px] rounded-full bg-[var(--lime)]/10 blur-3xl pointer-events-none"
        />
        <div className="container-main py-12 md:py-16 relative z-10 max-w-2xl">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white transition-colors mb-5"
          >
            <ArrowLeft size={14} />
            Back to MediTalk
          </Link>
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-3">
            <span className="w-6 h-px bg-[var(--lime)]" />
            Share your experience
          </span>
          <h1 className="font-['Outfit'] font-bold text-white tracking-tight text-3xl md:text-5xl mb-4 leading-[1.05]">
            {firstName ? `Hi ${firstName}, ` : "Hi, "}two minutes if you can spare them.
          </h1>
          <p className="text-base md:text-lg text-white/80 leading-relaxed max-w-xl">
            {ctx?.study_title ? `Thank you again for taking part in ${ctx.study_title}. ` : "Thank you again for taking part with us. "}
            Your honest words help the next patient or caregiver decide whether to join. Anything you share will appear with your first name only.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container-main max-w-2xl">
          <form
            onSubmit={submit}
            className="rounded-2xl border border-[var(--light-border)] bg-white p-6 md:p-8 space-y-6"
            data-testid="review-submit-form"
          >
            <div>
              <label className="block text-sm font-semibold text-[var(--charcoal)] mb-2">
                How was your experience overall?
              </label>
              <div
                className="flex items-center gap-1"
                role="radiogroup"
                aria-label="Rating from 1 to 5 stars"
              >
                {[1, 2, 3, 4, 5].map((n) => {
                  const filled = (hover || rating) >= n;
                  return (
                    <button
                      key={n}
                      type="button"
                      onMouseEnter={() => setHover(n)}
                      onMouseLeave={() => setHover(0)}
                      onFocus={() => setHover(n)}
                      onBlur={() => setHover(0)}
                      onClick={() => setRating(n)}
                      aria-label={`${n} star${n > 1 ? "s" : ""}`}
                      className="p-1 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--lime)]/40"
                      data-testid={`review-star-${n}`}
                    >
                      <Star
                        size={36}
                        className={
                          filled
                            ? "fill-[var(--lime)] text-[var(--lime-dark)] transition-transform hover:scale-110"
                            : "text-[var(--light-border)] transition-transform hover:scale-110"
                        }
                      />
                    </button>
                  );
                })}
                {rating > 0 && (
                  <span className="ml-3 text-sm font-semibold text-[var(--charcoal)]">
                    {["", "Poor", "OK", "Good", "Great", "Excellent"][rating]}
                  </span>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="review-content"
                className="block text-sm font-semibold text-[var(--charcoal)] mb-2"
              >
                In your own words
              </label>
              <textarea
                id="review-content"
                rows={6}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                maxLength={1500}
                placeholder="A few sentences about what you appreciated and what we could do better."
                className="w-full px-4 py-3 rounded-xl border border-[var(--light-border)] bg-white text-[15px] text-[var(--charcoal)] focus:outline-none focus:border-[var(--lime)] focus:ring-4 focus:ring-[var(--lime)]/20 resize-y"
                required
                data-testid="review-content"
              />
              <p className="text-[12px] text-[var(--text-muted)] mt-1">
                {content.length} / 1500 characters. Your review may be lightly
                edited for clarity before publication.
              </p>
            </div>

            <div className="rounded-xl border border-[var(--light-border)] bg-[var(--bone-soft)] p-4 text-[13px] text-[var(--text-muted)] leading-relaxed">
              We'll publish your review on{" "}
              <Link to="/reviews" className="text-[var(--charcoal)] font-semibold underline decoration-[var(--lime)] decoration-2 underline-offset-2">
                meditalk.world/reviews
              </Link>{" "}
              with your first name only , <strong>{firstName || "as you provided"}</strong>. We will not publish your email, phone number, or any of your study answers. We may lightly edit for clarity. You can ask us to remove the review at any time by writing to{" "}
              <a href="mailto:privacy@medicysltd.co.uk" className="text-[var(--charcoal)] font-semibold">
                privacy@medicysltd.co.uk
              </a>.
            </div>

            <div>
              <button
                type="submit"
                disabled={submitting || rating < 1 || content.trim().length < 10}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-[var(--lime)] text-[var(--dark-bg)] font-semibold text-sm hover:bg-[var(--lime-dark)] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-[0_8px_22px_-10px_rgba(196,247,75,0.7)]"
                data-testid="review-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    Share my review
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
